/** A single user-drawn redaction rectangle, in the captured image's own pixel coordinates. */
export interface RedactionBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Loads `imageBlob` onto a canvas, paints an opaque black rectangle over each box, and
 * re-exports the result as a new PNG `Blob`. This is the "manual" redaction path — the user
 * draws boxes over an already-captured screenshot in `ScreenshotPreview` — as opposed to
 * `SnapdomCapturer`'s selector-based masking, which happens *before* rasterization.
 *
 * jsdom implements neither real image decoding nor canvas rasterization, so the unit suite
 * (`redact.test.ts`) stubs `Image`/`HTMLCanvasElement` to exercise this function's own logic
 * (load → size canvas → draw source → fill N boxes → export) without real pixel ops; actual
 * rendered-pixel verification is deferred to Playwright (Task 10). The function itself is
 * written straightforwardly for a real browser — nothing here is jsdom-specific.
 */
export async function applyManualRedactions(imageBlob: Blob, boxes: RedactionBox[]): Promise<Blob> {
  const image = await loadImage(imageBlob);

  const canvas = document.createElement('canvas');
  canvas.width = image.width;
  canvas.height = image.height;

  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D | null;
  if (!ctx) {
    throw new Error('applyManualRedactions: 2D canvas context is unavailable in this environment.');
  }

  ctx.drawImage(image as unknown as CanvasImageSource, 0, 0, image.width, image.height);

  ctx.fillStyle = '#000000';
  for (const box of boxes) {
    ctx.fillRect(box.x, box.y, box.w, box.h);
  }

  return canvasToBlob(canvas);
}

/** Decodes `blob` into an `HTMLImageElement`, always revoking the object URL it creates. */
function loadImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('applyManualRedactions: failed to load the source image.'));
    };
    image.src = url;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('applyManualRedactions: canvas export produced no blob.'));
      }
    }, 'image/png');
  });
}
